import React, { useState, useEffect } from "react";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Edit, Trash2, Briefcase, Filter, Archive, RefreshCw, FolderArchive, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight, Search, Building2, Settings, Plus } from "lucide-react";
import { Combobox } from "../components/ui/combobox";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { canUserArchiveJobRole } from "../utils/jobrole-permissions";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { JobRole, Department, JobPosition } from "../types";

import { Can } from "../components/Can";
import { ACTIONS, RESOURCES } from "../constants/permissions.constants";

export default function JobRolesPage() {
  const { departments, jobRoles, users, deleteJobRole, archiveJobRole, unarchiveJobRole, jobTitles } = useData();
  const { currentUser } = useAuth();
  const [departmentFilter, setDepartmentFilter] = useState<string>("all");
  const [activeTab, setActiveTab] = useState<"active" | "archived">("active");
  const [showArchived, setShowArchived] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openArchive, setOpenArchive] = useState(false);
  const [openUnarchive, setOpenUnarchive] = useState(false);
  const [archiveReason, setArchiveReason] = useState("");
  const [actionJobRoleId, setActionJobRoleId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;
  const [jobPositions, setJobPositions] = useState<JobPosition[]>([]);

  // Update handlers to reset page
  const handleDepartmentFilter = (value: string) => {
    setDepartmentFilter(value);
    setCurrentPage(1);
  };

  // Values carry the raw department id — the same identifier the old Select
  // items used — so filter semantics are unchanged. "all" clears the filter.
  const departmentOptions = [
    { label: "All Departments", value: "all" },
    ...departments
      .filter(dept => !dept.deleted && (dept.isActive || dept.archived))
      .map((dept: Department) => ({
        label: `${dept.departmentCode} - ${dept.departmentName}${dept.archived ? " (Archived)" : ""}`,
        value: dept.id,
      })),
  ];

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleActiveTabChange = (value: string) => {
    setActiveTab(value as "active" | "archived");
    setCurrentPage(1);
  };

  useEffect(() => {
    const fetchPositions = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || "http://localhost:5001"}/api/jobroles/positions/all`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });
        const data = await response.json();
        if (data.success) {
          setJobPositions(data.positions || []);
        }
      } catch (error) {
        console.error("Error fetching job positions:", error);
      }
    };
    fetchPositions();
  }, []);

  // Helper functions for role-based permissions
  const canViewAllRoles = () => {
    return currentUser && ['Admin', 'Director', 'Manager'].includes(currentUser.userRole);
  };

  const canViewMyRole = () => {
    return currentUser && ['Admin', 'Director', 'Manager', 'Worker'].includes(currentUser.userRole);
  };

  // Check if user has any job role viewing permissions
  if (!canViewMyRole()) {
    return (
      <div className="flex items-center justify-center h-full">
        <p>You don't have permission to access this page</p>
      </div>
    );
  }

  const handleDelete = () => {
    try {
      deleteJobRole(actionJobRoleId);
      setActionJobRoleId("");
      setOpenDelete(false);
    } catch (error) {
      // Error will be shown via toast
      setOpenDelete(false);
    }
  };

  const handleArchive = async () => {
    if (!currentUser) return;

    try {
      const result = await archiveJobRole(actionJobRoleId, currentUser.id, archiveReason);

      if (result.success) {
        setOpenArchive(false);
        setArchiveReason("");
        setActionJobRoleId("");
      } else if (result.affectedUsers && result.affectedUsers.length > 0) {
        // Display error with affected users
        const affectedUsersList = result.affectedUsers.map(user => user.name).join(", ");
        toast.error(`Cannot archive: Job role is currently assigned to users: ${affectedUsersList}`);
        setOpenArchive(false);
      } else {
        toast.error("Failed to archive job role");
        setOpenArchive(false);
      }
    } catch (error) {
      toast.error("Failed to archive job role");
      setOpenArchive(false);
    }
  };

  const handleUnarchive = async () => {
    if (!currentUser) return;

    try {
      await unarchiveJobRole(actionJobRoleId, currentUser.id);
      setOpenUnarchive(false);
      setActionJobRoleId("");
    } catch (error) {
      toast.error("Failed to unarchive job role");
      setOpenUnarchive(false);
    }
  };

  const getDepartmentName = (role: JobRole) => {
    return role.department?.departmentName ||
      departments.find(d => String(d.id) === String(role.departmentId))?.departmentName ||
      "Unknown";
  };

  const getPositionStats = (roleId: string) => {
    const positionsForRole = jobPositions.filter(p => p.jobRole?.id === roleId || p.jobRoleId === roleId);
    const total = positionsForRole.length;
    const occupied = positionsForRole.filter(p => p.status === 'Filled').length;
    const percentage = total === 0 ? 0 : Math.round((occupied / total) * 100);
    return { total, occupied, percentage };
  };

  // Filter job roles based on permissions and selected filters
  const filteredJobRoles = jobRoles.filter(role => {
    // Apply role-based viewing permissions first
    if (canViewAllRoles()) {
      // Managers, Directors, and Admins can view all roles
      // Continue with other filters
    } else if (canViewMyRole() && currentUser?.userRole === 'Worker') {
      // Workers can only view their own job role
      if (role.title !== currentUser.jobRole) {
        return false;
      }
    }

    // Filter by archived status - handle undefined case
    const isArchived = role.archived === true;
    if (activeTab === "archived" && !isArchived) {
      return false;
    }

    // Then filter by department if not "all" (only for users who can view all roles)
    if (canViewAllRoles() && departmentFilter !== "all") {
      return role.departmentId === departmentFilter;
    }

    return true;
  });

  // Add search filter
  const filteredJobRolesWithSearch = filteredJobRoles.filter(role => {
    if (searchTerm && !role.title.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    return true;
  });

  console.log("departments:", departments);
  console.log("jobRoles:", jobRoles);

  return (
    <div className="space-y-6 p-2 md:p-0">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h1 className="text-2xl font-bold">Job Roles</h1>
        <div className="w-full md:w-auto flex items-center gap-2">
          {activeTab === "active" && (
            <Can I={ACTIONS.CREATE} a={RESOURCES.JOB_ROLE}>
              <Link to="/job-roles/new">
                <Button className="flex items-center gap-2 w-full md:w-auto">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Job Role
                </Button>
              </Link>
            </Can>
          )}
          <Can I={ACTIONS.READ} a={RESOURCES.JOB_ROLE_SETTINGS}>
            <Link to="/job-roles/settings">
              <Button variant="outline" className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Settings
              </Button>
            </Link>
          </Can>
        </div>
      </div>

      <Tabs defaultValue="active" className="w-full" onValueChange={handleActiveTabChange}>
        {/* <div className="w-full overflow-x-auto"> */}
        <TabsList className="bg-gray-100 p-1 rounded-full w-full md:w-fit">
          <TabsTrigger
            value="active"
            className="rounded-full flex-1 md:flex-none px-4 py-2 data-[state=active]:bg-white"
          >
            Active
          </TabsTrigger>
          <TabsTrigger
            value="archived"
            className="rounded-full flex-1 md:flex-none px-4 py-2 data-[state=active]:bg-white"
          >
            Archived
          </TabsTrigger>
        </TabsList>
        {/* </div> */}

        {/* Filters */}
        <div className="flex flex-col md:flex-row items-start md:items-center w-full gap-4 my-4 relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search job roles..."
            value={searchTerm}
            onChange={handleSearch}
            className="pl-10"
          />
          {canViewAllRoles() && (
            <div className="flex items-center w-full md:w-auto">
              {/* <Filter size={16} className="mr-2 text-muted-foreground" /> */}
              <Combobox
                options={departmentOptions}
                value={departmentFilter}
                onChange={(value) => handleDepartmentFilter(value || "all")}
                placeholder="Filter by Department"
                searchPlaceholder="Search departments..."
                className="w-full md:w-[250px]"
              />
            </div>
          )}
        </div>

        <TabsContent value="active">
          {/* <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                Active Job Roles
              </CardTitle>
            </CardHeader>
            <CardContent> */}
          {/* Mobile View: List of Cards */}
          <div className="md:hidden space-y-4">
            {filteredJobRolesWithSearch.filter(role => !role.archived).length > 0 ? (
              filteredJobRolesWithSearch
                .filter(role => !role.archived)
                .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                .map((role) => (
                  <Card key={role.id} className="p-4">
                    <div className="flex flex-col gap-3">
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex flex-col gap-1 flex-grow min-w-0">
                          <Link to={`/job-roles/${role.id}`} className="font-semibold text-primary hover:underline truncate block">{role.title}</Link>
                          <span className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                            <Building2 size={14} />{getDepartmentName(role)}
                          </span>
                        </div>

                        <div className="flex items-center flex-shrink-0">
                          <Can I={ACTIONS.UPDATE} a={RESOURCES.JOB_ROLE}>
                            <Link to={`/job-roles/${role.id}/edit`}>
                              <Button variant="ghost" size="icon">
                                <Edit size={18} />
                              </Button>
                            </Link>
                          </Can>

                          <Can I={ACTIONS.ARCHIVE} a={RESOURCES.JOB_ROLE}>
                            <Button variant="ghost" size="icon" onClick={() => { setActionJobRoleId(role.id); setOpenArchive(true); }}>
                              <Archive size={18} />
                            </Button>
                          </Can>

                          <Can I={ACTIONS.DELETE} a={RESOURCES.JOB_ROLE}>
                            <Button variant="ghost" size="icon" className="text-destructive" onClick={() => { setActionJobRoleId(role.id); setOpenDelete(true); }}>
                              <Trash2 size={18} />
                            </Button>
                          </Can>

                        </div>

                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2">{role.description || "No description provided."}</p>
                    </div>
                  </Card>
                ))
            ) : (
              <p className="text-center text-muted-foreground py-8">No active job roles found.</p>
            )}
          </div>

          {/* Desktop View: Table */}
          <Card className="hidden md:block">
            <CardContent className="p-3">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Positions Occupied</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredJobRolesWithSearch
                    .filter(role => !role.archived)
                    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                    .map((role) => (
                      <TableRow key={role.id}>
                        <TableCell>
                          <Link to={`/job-roles/${role.id}`} className="font-medium hover:underline text-primary flex items-center gap-2">
                            <Briefcase size={16} />
                            {role.title}
                          </Link>
                        </TableCell>
                        <TableCell>{getDepartmentName(role)}</TableCell>
                        <TableCell className="max-w-md truncate">{role.description}</TableCell>
                        <TableCell>
                          {(() => {
                            const { total, occupied, percentage } = getPositionStats(role.id);
                            return (
                              <div className="flex items-center gap-2">
                                <Progress value={percentage} className="w-24 h-2" />
                                <span className="text-xs text-muted-foreground">{occupied}/{total}</span>
                              </div>
                            );
                          })()}
                        </TableCell>
                        <Can I={ACTIONS.UPDATE} a={RESOURCES.JOB_ROLE}>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Link to={`/job-roles/${role.id}/edit`}>
                                <Button variant="outline" size="icon">
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </Link>

                              <Can I={ACTIONS.ARCHIVE} a={RESOURCES.JOB_ROLE}>
                                <Button variant="outline" size="icon" onClick={() => { setActionJobRoleId(role.id); setOpenArchive(true); }}>
                                  <Archive className="h-4 w-4" />
                                </Button>
                              </Can>

                              <Can I={ACTIONS.DELETE} a={RESOURCES.JOB_ROLE}>
                                <Button variant="outline" size="icon" className="text-destructive" onClick={() => { setActionJobRoleId(role.id); setOpenDelete(true); }}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </Can>

                            </div>
                          </TableCell>
                        </Can>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Add Pagination */}
          {filteredJobRolesWithSearch.filter(role => !role.archived).length > 0 && (
            <div className="mt-4">
              <div className="flex items-center justify-center space-x-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                {Array.from({ length: Math.ceil(filteredJobRolesWithSearch.filter(role => !role.archived).length / itemsPerPage) }, (_, i) => i + 1)
                  .filter(pageNum => {
                    const maxPages = Math.ceil(filteredJobRolesWithSearch.filter(role => !role.archived).length / itemsPerPage);
                    if (maxPages <= 7) return true;
                    if (pageNum === 1 || pageNum === maxPages) return true;
                    if (pageNum >= currentPage - 2 && pageNum <= currentPage + 2) return true;
                    return false;
                  })
                  .map((pageNum, i, array) => {
                    if (i > 0 && array[i - 1] !== pageNum - 1) {
                      return (
                        <React.Fragment key={`ellipsis-${pageNum}`}>
                          <span className="px-2">...</span>
                          <Button
                            variant={currentPage === pageNum ? "outline" : "ghost"}
                            onClick={() => setCurrentPage(pageNum)}
                            className="size-9"
                          >
                            {pageNum}
                          </Button>
                        </React.Fragment>
                      );
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "outline" : "ghost"}
                        onClick={() => setCurrentPage(pageNum)}
                        className="size-9"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setCurrentPage(prev => Math.min(Math.ceil(filteredJobRolesWithSearch.filter(role => !role.archived).length / itemsPerPage), prev + 1))}
                  disabled={currentPage === Math.ceil(filteredJobRolesWithSearch.filter(role => !role.archived).length / itemsPerPage)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setCurrentPage(Math.ceil(filteredJobRolesWithSearch.filter(role => !role.archived).length / itemsPerPage))}
                  disabled={currentPage === Math.ceil(filteredJobRolesWithSearch.filter(role => !role.archived).length / itemsPerPage)}
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
          {/* </CardContent>
          </Card> */}
        </TabsContent>

        <TabsContent value="archived">
          <Card>
            <CardHeader>
              <CardTitle>
                {!canViewAllRoles() && currentUser?.userRole === 'Worker'
                  ? "My Job Role (Archived)"
                  : departmentFilter === "all"
                    ? "Archived Job Roles"
                    : `Archived Job Roles - ${departments.find(d => d.id === departmentFilter)?.departmentName || "Unknown Department"}`}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Positions Occupied</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredJobRolesWithSearch
                    .filter(role => role.archived)
                    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                    .map((role) => (
                      <TableRow key={role.id}>
                        <TableCell>
                          <Link to={`/job-roles/${role.id}`} className="hover:underline">
                            <div className="flex items-center gap-2">
                              <Briefcase size={16} />
                              {role.title}
                              <Badge variant="outline" className="gap-1 border-amber-200 bg-amber-50 text-amber-800">
                                <Archive size={12} />
                                Archived
                              </Badge>
                            </div>
                          </Link>
                        </TableCell>
                        <TableCell>{(() => {
                          // Find the department object
                          const dept = role.department || departments.find(d => String(d.id) === String(role.departmentId));
                          if (!dept) return "Unknown";
                          return (
                            <Link to={`/departments/${dept.departmentCode}`} className="hover:underline text-primary">
                              {dept.departmentCode} - {dept.departmentName}
                            </Link>
                          );
                        })()}</TableCell>
                        <TableCell className="max-w-md truncate">{role.description}</TableCell>
                        <TableCell>
                          {(() => {
                            const { total, occupied, percentage } = getPositionStats(role.id);
                            return (
                              <div className="flex items-center gap-2">
                                <Progress value={percentage} className="w-24 h-2" />
                                <span className="text-xs text-muted-foreground">{occupied}/{total}</span>
                              </div>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">Archived</Badge>
                        </TableCell>
                        <Can I={ACTIONS.UNARCHIVE} a={RESOURCES.JOB_ROLE}>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => {
                                  setActionJobRoleId(role.id);
                                  setOpenUnarchive(true);
                                }}
                              >
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </Can>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>

              {/* Add Pagination for Archived */}
              {filteredJobRolesWithSearch.filter(role => role.archived).length > 0 && (
                <div className="mt-4">
                  <div className="flex items-center justify-center space-x-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronsLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    {Array.from({ length: Math.ceil(filteredJobRolesWithSearch.filter(role => role.archived).length / itemsPerPage) }, (_, i) => i + 1)
                      .filter(pageNum => {
                        const maxPages = Math.ceil(filteredJobRolesWithSearch.filter(role => role.archived).length / itemsPerPage);
                        if (maxPages <= 7) return true;
                        if (pageNum === 1 || pageNum === maxPages) return true;
                        if (pageNum >= currentPage - 2 && pageNum <= currentPage + 2) return true;
                        return false;
                      })
                      .map((pageNum, i, array) => {
                        if (i > 0 && array[i - 1] !== pageNum - 1) {
                          return (
                            <React.Fragment key={`ellipsis-${pageNum}`}>
                              <span className="px-2">...</span>
                              <Button
                                variant={currentPage === pageNum ? "outline" : "ghost"}
                                onClick={() => setCurrentPage(pageNum)}
                                className="size-9"
                              >
                                {pageNum}
                              </Button>
                            </React.Fragment>
                          );
                        }
                        return (
                          <Button
                            key={pageNum}
                            variant={currentPage === pageNum ? "outline" : "ghost"}
                            onClick={() => setCurrentPage(pageNum)}
                            className="size-9"
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setCurrentPage(prev => Math.min(Math.ceil(filteredJobRolesWithSearch.filter(role => role.archived).length / itemsPerPage), prev + 1))}
                      disabled={currentPage === Math.ceil(filteredJobRolesWithSearch.filter(role => role.archived).length / itemsPerPage)}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setCurrentPage(Math.ceil(filteredJobRolesWithSearch.filter(role => role.archived).length / itemsPerPage))}
                      disabled={currentPage === Math.ceil(filteredJobRolesWithSearch.filter(role => role.archived).length / itemsPerPage)}
                    >
                      <ChevronsRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Job Role Dialog */}
      <Dialog open={openDelete} onOpenChange={setOpenDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-lg">Delete Job Role</DialogTitle>
            <DialogDescription className="text-sm">
              This will permanently remove the job role and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {jobRoles.find(role => role.id === actionJobRoleId)?.title && (
            <div className="py-4 space-y-4">
              {users.some(user => user.jobRole === jobRoles.find(role => role.id === actionJobRoleId)?.title) ? (
                <>
                  <p className="text-sm text-black">
                    This Job Role ({jobRoles.find(role => role.id === actionJobRoleId)?.title}) is being occupied by:
                  </p>
                  <ul className="list-disc pl-6 text-sm">
                    {users
                      .filter(user => user.jobRole === jobRoles.find(role => role.id === actionJobRoleId)?.title)
                      .map(user => (
                        <li key={user.id}>{user.name}</li>
                      ))}
                  </ul>
                  <p className="text-sm">Resolve this and return here to delete this job role.</p>
                </>
              ) : (
                <>
                  <p className="text-sm">Are you sure you want to delete the job role "{jobRoles.find(role => role.id === actionJobRoleId)?.title}"?</p>
                  <p className="text-sm text-red-600">This action cannot be undone.</p>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={users.some(user => user.jobRole === jobRoles.find(role => role.id === actionJobRoleId)?.title)}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Job Role Dialog */}
      <Dialog open={openArchive} onOpenChange={setOpenArchive}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-lg">Archive Job Role</DialogTitle>
            <DialogDescription className="text-sm">
              Archiving will hide this job role from active use. It will remain for historical reference.
            </DialogDescription>
          </DialogHeader>
          {jobRoles.find(role => role.id === actionJobRoleId)?.title && (
            <div className="py-4 space-y-4">
              {users.some(user => user.jobRole === jobRoles.find(role => role.id === actionJobRoleId)?.title) ? (
                <>
                  <p className="text-sm text-black">
                    This Job Role ({jobRoles.find(role => role.id === actionJobRoleId)?.title}) is being occupied by:
                  </p>
                  <ul className="list-disc pl-6 text-sm">
                    {users
                      .filter(user => user.jobRole === jobRoles.find(role => role.id === actionJobRoleId)?.title)
                      .map(user => (
                        <li key={user.id}>{user.name}</li>
                      ))}
                  </ul>
                  <p className="text-sm">Resolve this and return here to archive this job role.</p>
                </>
              ) : (
                <>
                  <p className="text-sm">Are you sure you want to archive the job role "{jobRoles.find(role => role.id === actionJobRoleId)?.title}"?</p>
                  <div className="space-y-2">
                    <Label htmlFor="archiveReason" className="text-sm">Reason for Archiving (Optional)</Label>
                    <Textarea
                      id="archiveReason"
                      placeholder="Enter the reason for archiving this job role..."
                      value={archiveReason}
                      onChange={(e) => setArchiveReason(e.target.value)}
                      rows={3}
                      className="text-sm"
                    />
                  </div>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenArchive(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleArchive}
              disabled={users.some(user => user.jobRole === jobRoles.find(role => role.id === actionJobRoleId)?.title)}
            >
              Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unarchive Job Role Dialog */}
      <Dialog open={openUnarchive} onOpenChange={setOpenUnarchive}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-lg">Unarchive Job Role</DialogTitle>
            <DialogDescription className="text-sm">
              Unarchiving will make this job role active and available for use again.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm">Are you sure you want to unarchive the job role "{jobRoles.find(role => role.id === actionJobRoleId)?.title}"?</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenUnarchive(false)}>
              Cancel
            </Button>
            <Button onClick={handleUnarchive}>
              Unarchive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}